import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useCategoryOptions,
  useSubcategoryOptions,
} from "@features/cx/consumerManagement/hooks";
import { usePremises, usePlan } from "@features/cx/reconnect/hooks";

interface PremisePlanFilterProps {
  form: UseFormReturn<any>;
  onApply: (data: any) => void;
  onReset: () => void;
}

export const PremisePlanFilter: React.FC<PremisePlanFilterProps> = ({
  form,
  onApply,
  onReset,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const watchCategory = form.watch("category");

  // Fetch Categories
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategoryOptions(Number(remoteUtilityId));

  const categoryOptions = useMemo(() => {
    return (
      categoriesData?.result?.map((cat: any) => ({
        label: cat.name,
        value: cat.code,
      })) || []
    );
  }, [categoriesData]);

  // Fetch Sub Categories based on selected category
  const { data: subCategoriesData, isLoading: isLoadingSubCategories } =
    useSubcategoryOptions(Number(remoteUtilityId), watchCategory);

  const subCategoryOptions = useMemo(() => {
    return (
      subCategoriesData?.result?.map((sub: any) => ({
        label: sub.name,
        value: sub.code,
      })) || []
    );
  }, [subCategoriesData]);

  // Fetch Premises
  const { data: premisesData, isLoading: isLoadingPremises } = usePremises(
    Number(remoteUtilityId),
  );

  const premiseOptions = useMemo(() => {
    return (
      premisesData?.map((premise: any) => ({
        label: premise.name,
        value: premise.code,
      })) || []
    );
  }, [premisesData]);

  // Fetch Plans
  const { data: plansData, isLoading: isLoadingPlans } = usePlan(
    remoteUtilityId,
  );

  const planOptions = useMemo(() => {
    return (
      plansData?.map((plan: any) => ({
        label: plan.planName,
        value: plan.id,
      })) || []
    );
  }, [plansData]);

  const fields: FormField[] = [
    {
      name: "premise",
      label: "Premise",
      type: "select",
      placeholder: isLoadingPremises ? "Loading..." : "All Premises",
      options: premiseOptions,
    },
    {
      name: "plan",
      label: "Plan",
      type: "select",
      placeholder: isLoadingPlans ? "Loading..." : "All Plans",
      options: planOptions,
    },
    {
      name: "category",
      label: "Category",
      type: "select",
      placeholder: isLoadingCategories ? "Loading..." : "All Categories",
      options: categoryOptions,
    },
    {
      name: "sub_category",
      label: "Sub Category",
      type: "select",
      placeholder: !watchCategory
        ? "Select Category first"
        : isLoadingSubCategories
        ? "Loading..."
        : "All Sub-Categories",
      options: subCategoryOptions,
      disabled: !watchCategory,
    },
  ];

  const handleReset = () => {
    form.reset({
      premise: "",
      plan: "",
      category: "",
      sub_category: "",
    });
    onReset();
  };

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={fields}
        form={form}
        gridColumns={4}
        responsiveColumns={{ sm: 1, md: 2, lg: 4 }}
        onSubmit={onApply}
        onReset={handleReset}
        config={{
          showResetButton: true,
          resetButtonText: "Reset",
          submitButtonText: "Apply Filters",
          classes: {
            buttonContainer: "flex justify-end gap-2 pt-2 border-t",
          },
        }}
      />
    </div>
  );
};
